import React from 'react';
import { Bell, Trash, AlertCircle } from 'lucide-react';
import NotificationList from '@/components/notifications/NotificationList';
import useNotificationsPage from '@/hooks/notifications/use-notifications-page';
import Container from '@/components/ui/Container';
import DashboardHeader from '@/components/dashboard/DashboardHeader';

const Notifications: React.FC = () => {
  const {
    filteredNotifications,
    handleRemoveNotification,
    handleMarkAsRead,
    handleRemoveAllNotifications,
    isLoading,
    error
  } = useNotificationsPage();

  // Loading state
  if (isLoading) {
    return (
      <Container>
        <DashboardHeader 
          title="Notifications"
          description="Stay updated with all your important alerts and messages"
          icon={<Bell size={24} />}
        />
        
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className="text-slate-600 dark:text-slate-400">Loading notifications...</p>
          </div>
        </div>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container>
        <DashboardHeader 
          title="Notifications"
          description="Stay updated with all your important alerts and messages"
          icon={<Bell size={24} />}
        />
        
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center space-y-4 text-center">
            <div className="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/30 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
              Failed to load notifications
            </h3>
            <p className="text-slate-600 dark:text-slate-400 max-w-md">
              There was an error loading your notifications. Please refresh the page or try again later.
            </p>
            <button 
              onClick={() => window.location.reload()} 
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium"
            >
              Refresh Page
            </button>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <DashboardHeader 
        title="Notifications"
        description="Stay updated with all your important alerts and messages"
        icon={<Bell size={24} />}
        primaryAction={filteredNotifications.length > 0 ? {
          label: "Clear All",
          icon: Trash,
          onClick: handleRemoveAllNotifications,
          variant: "primary"
        } : undefined}
      />
      
      <NotificationList
        notifications={filteredNotifications}
        onRemoveNotification={handleRemoveNotification}
        onMarkAsRead={handleMarkAsRead}
      />
    </Container>
  );
};

export default Notifications; 